import React from 'react';
import { Typography, Card, Divider } from 'antd';
import styled from 'styled-components/macro';
import {
    Entity,
    EntityType,
    FacetFilterInput,
    FacetMetadata,
    MatchedField,
    SearchSuggestion,
} from '../../types.generated';
import { SearchResultsRecommendations } from './SearchResultsRecommendations';
import SearchExtendedMenu from '../entity/shared/components/styled/search/SearchExtendedMenu';
import { SearchSelectBar } from '../entity/shared/components/styled/search/SearchSelectBar';
import { SearchResultList } from './SearchResultList';
import TabToolbar from '../entity/shared/components/styled/TabToolbar';
import { EntityAndType } from '../entity/shared/types';
import { isListSubset } from '../entity/shared/utils';
import { ErrorSection } from '../shared/error/ErrorSection';
import { UnionType } from './utils/constants';
import { SearchFiltersSection } from './SearchFiltersSection';
import { generateOrFilters } from './utils/generateOrFilters';
import { useUserContext } from '../context/useUserContext';
import { combineSiblingsInSearchResults } from './utils/combineSiblingsInSearchResults';
import SearchQuerySuggester from './suggestions/SearchQuerySugggester';
import { PaginationBar } from '../entity/shared/tabs/Glossary/PaginationBar';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { useTranslation } from '../../i18n/useTranslation';

const SearchBody = styled.div`
    display: flex;
    min-height: 100%;
    flex: 1;
    margin-top: 4px;
    overflow: auto;
`;

const ResultContainer = styled.div`
    flex: 1;

    .ant-list {
        height: 100%;
    }
`;

const PaginationInfoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const StyledTabToolbar = styled(TabToolbar)`
    padding-left: 32px;
    padding-right: 32px;
`;

const SearchMenuContainer = styled.div``;

interface Props {
    unionType?: UnionType;
    query: string;
    page: number;
    searchResponse?: {
        start: number;
        count: number;
        total: number;
        searchResults?: {
            entity: Entity;
            matchedFields: MatchedField[];
        }[];
    } | null;
    facets?: Array<FacetMetadata> | null;
    selectedFilters: Array<FacetFilterInput>;
    loading: boolean;
    error: any;
    onChangeFilters: (filters: Array<FacetFilterInput>) => void;
    onChangeUnionType: (unionType: UnionType) => void;
    onChangePage: (page: number) => void;
    numResultsPerPage: number;
    entityFilters: EntityType[];
    setNumResultsPerPage: (numResults: number) => void;
    isSelectMode: boolean;
    selectedEntities: EntityAndType[];
    suggestions: SearchSuggestion[];
    setSelectedEntities: (entities: EntityAndType[]) => void;
    setIsSelectMode: (showSelectMode: boolean) => any;
    onChangeSelectAll: (selected: boolean) => void;
    refetch: () => void;
}

export const SearchResults = ({
    unionType = UnionType.AND,
    query,
    page,
    searchResponse,
    facets,
    selectedFilters,
    loading,
    error,
    onChangeUnionType,
    onChangeFilters,
    onChangePage,
    entityFilters,
    numResultsPerPage,
    setNumResultsPerPage,
    isSelectMode,
    selectedEntities,
    suggestions,
    setIsSelectMode,
    setSelectedEntities,
    onChangeSelectAll,
    refetch,
}: Props) => {
    const { t } = useTranslation();
    const pageStart = searchResponse?.start || 0;
    const pageSize = searchResponse?.count || 0;
    const totalResults = searchResponse?.total || 0;
    const lastResultIndex = pageStart + pageSize > totalResults ? totalResults : pageStart + pageSize;
    const authenticatedUserUrn = useUserContext().user?.urn;
    const combinedSiblingSearchResults = combineSiblingsInSearchResults(searchResponse?.searchResults);

    const selectedEntityUrns = selectedEntities.map((entity) => entity.urn);

    // Calculate selectable URNs (excluding read-only datasets)
    const selectableUrns = combinedSiblingSearchResults
        .filter((result) => {
            const isReadOnly =
                result.entity.type === EntityType.Dataset && (result.entity as any)?.readOnly?.readOnly === true;
            return !isReadOnly;
        })
        .map((result) => result.entity.urn);

    // Calculate isSelectAll: all selectable entities are selected
    const isSelectAll = selectedEntities.length > 0 && isListSubset(selectableUrns, selectedEntityUrns);

    // Calculate indeterminate state for select all checkbox
    const isIndeterminate = () => {
        const selectedCount = selectableUrns.filter((urn) => selectedEntityUrns.includes(urn)).length;
        return selectedCount > 0 && selectedCount < selectableUrns.length;
    };

    return (
        <>
            {loading && <ComponentSpinner />}

            <div>
                <Divider className="ma-0" />
                <Card className="pa-2 my-1">
                    <PaginationInfoContainer>
                        <Typography.Paragraph className="f-text-content mb-0">
                            {t('Showing')} &nbsp;
                            <b>
                                {lastResultIndex > 0 ? (page - 1) * pageSize + 1 : 0} - {lastResultIndex}
                            </b>{' '}
                            {t('of')} <b>{totalResults}</b> {t('results')}
                        </Typography.Paragraph>
                        <SearchMenuContainer>
                            <SearchExtendedMenu
                                query={query}
                                totalResults={totalResults}
                                entityFilters={entityFilters}
                                setShowSelectMode={setIsSelectMode}
                                filters={generateOrFilters(unionType, selectedFilters)}
                            />
                        </SearchMenuContainer>
                    </PaginationInfoContainer>
                </Card>
                <Divider className="ma-0" />
                <SearchBody>
                    {false && (
                        <div>
                            <SearchFiltersSection
                                filters={facets}
                                selectedFilters={selectedFilters}
                                unionType={unionType}
                                loading={loading}
                                onChangeFilters={onChangeFilters}
                                onChangeUnionType={onChangeUnionType}
                            />
                        </div>
                    )}
                    <ResultContainer>
                        {isSelectMode && (
                            <StyledTabToolbar className="f-layout-wrap-xs f-px-3-xs">
                                <SearchSelectBar
                                    isSelectAll={isSelectAll}
                                    isIndeterminate={isIndeterminate()}
                                    selectedEntities={selectedEntities}
                                    onChangeSelectAll={onChangeSelectAll}
                                    onCancel={() => setIsSelectMode(false)}
                                    refetch={refetch}
                                />
                            </StyledTabToolbar>
                        )}
                        {(error && <ErrorSection />) ||
                            (!loading && (
                                <>
                                    {totalResults > 0 && <SearchQuerySuggester suggestions={suggestions} />}
                                    <SearchResultList
                                        query={query}
                                        searchResults={combinedSiblingSearchResults}
                                        totalResultCount={totalResults}
                                        isSelectMode={isSelectMode}
                                        selectedEntities={selectedEntities}
                                        setSelectedEntities={setSelectedEntities}
                                        suggestions={suggestions}
                                    />
                                </>
                            ))}
                    </ResultContainer>
                </SearchBody>
                {!loading && (
                    <>
                        <PaginationBar
                            page={page}
                            showSizeChanger
                            loading={loading}
                            totalCount={totalResults}
                            pageSize={numResultsPerPage}
                            onChangePage={onChangePage}
                            onShowSizeChange={setNumResultsPerPage}
                        />

                        {authenticatedUserUrn && (
                            <>
                                <Divider className="ma-0" />
                                <SearchResultsRecommendations
                                    userUrn={authenticatedUserUrn}
                                    query={query}
                                    filters={selectedFilters}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
